/**
 * Form Data Transformers (T094)
 * Utilities for transforming editor form data to provider format for preview display
 * 
 * Purpose:
 * - Convert ProviderEditorFormData to ProviderProfile format
 * - Enable Preview tab to display form state using ProviderDetails components
 * - Handle all field types: strings, numbers, arrays, objects, nullable fields
 * - Generate placeholder IDs for preview mode
 */

import type { ProviderEditorFormData } from '../schemas';
import type { ProviderProfile } from '@/features/provider-directory/types/provider-profile.types';
import type { TaxonomyOption } from '@/features/admin-portal/services/taxonomyApi';
import { getFileUrl } from '@/shared/hooks/useFileUpload';

/**
 * Transform form data to ProviderProfile format for preview rendering
 * 
 * @param formData - Current form state from React Hook Form
 * @param referenceData - Optional reference data for looking up names from IDs
 * @returns ProviderProfile object that can be passed to ProviderDetails components
 * 
 * Features:
 * - Converts nested form structure (user, provider) to flat provider with embedded user
 * - Converts empty strings to null for optional fields
 * - Converts undefined to null or empty arrays as appropriate
 * - Generates preview IDs (not persisted)
 * - Includes timestamps and mock data for preview-only fields
 * - Looks up actual names from reference data when available
 * 
 * @example
 * const provider = transformFormDataToProvider(formData, {
 *   categories: categoriesData,
 *   specialties: specialtiesData,
 *   expertise: expertiseData
 * });
 * <ProviderDetailsHero provider={provider} />
 */
export function transformFormDataToProvider(
  formData: ProviderEditorFormData,
  referenceData?: {
    categories?: TaxonomyOption[];
    specialties?: TaxonomyOption[];
    expertise?: TaxonomyOption[];
    /** SQL-only field — not in the form. Preview needs it for Schedule Call. */
    intakeFormUrl?: string | null;
  }
): ProviderProfile {
  // Generate preview IDs (not persisted to database)
  const previewId = `preview-${Date.now()}`;
  const previewUserId = `preview-user-${Date.now()}`;

  // Helper: Convert empty string to null
  const emptyToNull = (value?: string): string | null => {
    return value && value.trim() !== '' ? value : null;
  };

  // Helper: Convert undefined/empty arrays to empty array
  const ensureArray = <T,>(value?: T[]): T[] => {
    return value || [];
  };

  // Helper: Convert undefined to null for objects
  const ensureObject = <T,>(value?: T): T | null => {
    return value || null;
  };

  // Helper: Hydrate S3 file key to full URL (mirrors backend hydrateImageUrl)
  const hydrateImageUrl = (fileKey?: string | null): string | null => {
    if (!fileKey || fileKey.trim() === '') {
      return null;
    }
    
    // If already a full URL (starts with http), return as is
    if (fileKey.startsWith('http://') || fileKey.startsWith('https://')) {
      return fileKey;
    }
    
    // Convert S3 file key to full URL using CloudFront/S3
    return getFileUrl(fileKey);
  };

  // Transform provider data to ProviderProfile format
  const provider: ProviderProfile = {
    // Core Identity
    id: previewId,
    userId: previewUserId,
    companyName: formData.provider.companyName,
    
    // Hero Section
    tagline: emptyToNull(formData.provider.tagline),
    mediaType: formData.provider.mediaType as 'image' | 'video' | null || null,
    heroMediaUrl: hydrateImageUrl(formData.provider.heroMediaUrl),
    
    // Reviews & Ratings (preview mode - use defaults)
    placeId: emptyToNull(formData.provider.placeId),
    averageRating: null,
    totalReviewCount: 0,
    googleReviews: null,
    googleReviewsUrl: null,
    internalRating: null,
    
    // Pricing
    priceRangeIndicator: formData.provider.priceRangeIndicator || null,
    priceDetailsText: emptyToNull(formData.provider.priceDetailsText),
    
    // Operating Info
    hoursDescription: emptyToNull(formData.provider.hoursDescription),
    establishedYear: formData.provider.establishedYear ?? null,
    yearsInBusiness: formData.provider.yearsInBusiness ?? null,
    
    // Calculated Fields (preview mode - use defaults)
    servedCounties: [],
    primaryCategory: null,
    serviceDeliveryTypes: formData.provider.serviceRanges
      ?.map(range => {
        switch (range) {
          case 'IN_HOME': return 'In-Home';
          case 'VIRTUAL': return 'Virtual';
          case 'IN_OFFICE': return 'In-Office';
          default: return range;
        }
      })
      .join(' • ') || null,
    
    // Contact
    phoneNumber: emptyToNull(formData.provider.phoneNumber),
    websiteUrl: emptyToNull(formData.provider.websiteUrl),
    socialHandles: ensureObject(formData.provider.socialHandles),
    
    // Professional Details
    professionalTitle: emptyToNull(formData.provider.professionalTitle),
    companyOverview: emptyToNull(formData.provider.companyOverview),
    providerBio: emptyToNull(formData.provider.providerBio),
    languages: ensureArray(formData.provider.languages),
    certificationsArray: ensureArray(formData.provider.certificationsArray),
    professionalLicenses: ensureArray(formData.provider.professionalLicenses),
    awardsRecognitions: ensureArray(formData.provider.awardsRecognitions),
    yearsExperience: formData.provider.yearsExperience ?? null,
    professionalAssociations: ensureArray(formData.provider.professionalAssociations),
    carePhilosophy: emptyToNull(formData.provider.carePhilosophy),
    whyThisWork: emptyToNull(formData.provider.whyThisWork),
    introductionVideoUrl: hydrateImageUrl(formData.provider.introductionVideoUrl), // Hydrate video URLs too
    continuingEducation: emptyToNull(formData.provider.continuingEducation),
    emergencyResponseAvailable: formData.provider.emergencyResponseAvailable ?? false,
    emergencyResponseDetails: emptyToNull(formData.provider.emergencyResponseDetails),
    targetAgeRange: emptyToNull(formData.provider.targetAgeRange),
    
    // Location
    registeredStates: [],
    
    // Related Data
    // Services catalog from form (Services Catalog Extension)
    services: (formData.servicesCatalog || [])
      .filter(service => service.isActive) // Only include active services in preview
      .map(service => ({
        id: service.id || `temp-${Date.now()}`,
        serviceName: service.serviceName,
        serviceDescription: service.serviceDescription || null,
        servicePrice: service.servicePrice || null,
        priceType: service.priceType || null,
        priceRangeIndicator: service.priceRangeIndicator || null,
        isActive: service.isActive,
        displayOrder: service.displayOrder,
      })),
    badges: [],
    // Media gallery from form (T095 - media display with ordering and is_primary)
    media: (formData.mediaGallery || []).map(item => ({
      id: item.id,
      mediaType: item.mediaType,
      mediaUrl: hydrateImageUrl(item.mediaUrl) || item.mediaUrl, // Hydrate S3 key to full URL
      caption: item.caption || null,
      isPrimary: item.isPrimary,
      displayOrder: item.displayOrder,
    })),
    
    // Status
    vettingStatus: formData.provider.vettingStatus || 'PENDING',
    
    // Partner Company (null for preview mode)
    partnerCompany: null,
    
    // Existing fields (for backward compatibility)
    businessAddress: emptyToNull(formData.provider.businessAddress),
    hideBusinessAddress: formData.provider.hideBusinessAddress ?? false,
    latitude: formData.provider.latitude ?? null,
    longitude: formData.provider.longitude ?? null,
    serviceOfferings: emptyToNull(formData.provider.serviceOfferings),
    calendlyUrl: emptyToNull(formData.provider.calendlyUrl),
    intakeFormUrl: referenceData?.intakeFormUrl ?? null,
    newClientAcceptance: formData.provider.newClientAcceptance ?? false,
    serviceRanges: ensureArray(formData.provider.serviceRanges),
    
    // Relationships (as arrays with id/name for ProviderProfile format)
    // Look up actual names from reference data if available, otherwise use placeholder
    categories: (formData.categoryIds || []).map(id => {
      const category = referenceData?.categories?.find(c => c.id === id);
      return { id, name: category?.name || 'Category' };
    }),
    specialties: (formData.specialties || []).map((item) => {
      const specialty = referenceData?.specialties?.find((s) => s.id === item.specialtyId);
      return { id: item.specialtyId, name: specialty?.name || 'Specialty' };
    }),
    expertise: (formData.expertiseIds || []).map(id => {
      const exp = referenceData?.expertise?.find(e => e.id === id);
      return { id, name: exp?.name || 'Expertise' };
    }),
    
    // Embedded user
    user: {
      id: previewUserId,
      firstName: formData.user.firstName,
      lastName: formData.user.lastName || undefined,
      email: formData.user.email,
      profilePhoto: hydrateImageUrl(formData.user.profilePhoto),
    },
  };

  return provider;
}

